import { nullToUndefined, q } from 'groqd'
import { z } from 'zod'

export const blogPostSlugQuery = q('*')
  .filter('_type == "blogPost" && _id == $id')
  .grab({
    _type: q.literal('blogPost'),
    slug: ['slug[$lang].current', q.string()],
  })
  .slice(0)

export const websiteQuery = q('*')
  .filter('_type == "website.route" && _id == $id')
  .grab({
    _type: q.literal('website.route'),
    market: q.string(),
    slug: ['slug[$lang].current', q.string()],
    folder: ['folder[$lang]', q.string().nullable()],
    redirectActive: q.boolean().nullable(),
    redirectTarget: q.string().nullable(),
  })
  .slice(0)

export const webUrlSelector = (name: string) => {
  return q(name)
    .filter()
    .select({
      ["_type == 'blogPost_2'"]: {
        _key: q.string(),
        _type: q.literal('blogPost_2'),
        post: q.object({
          _type: q.literal('reference'),
          _ref: q.string(),
        }),
        anchor: q.string().nullable(),
      },
      ["_type == 'product'"]: {
        _key: q.string(),
        _type: q.literal('product'),
        abstractProduct: q.object({
          _type: q.literal('reference'),
          _ref: q.string(),
        }),
      },
      ["_type == 'internalWebsite_2'"]: {
        _key: q.string(),
        _type: q.literal('internalWebsite_2'),
        route: q.object({
          _type: q.literal('reference'),
          _ref: q.string(),
        }),
        anchor: q.string().nullable(),
      },
      ["_type == 'buyingFlow'"]: {
        _key: q.string(),
        _type: q.literal('buyingFlow'),
        flow: nullToUndefined(
          q
            .object({
              _type: q.literal('reference'),
              _ref: q.string(),
            })
            .optional()
        ),
        campaign: nullToUndefined(
          q
            .object({
              _type: q.literal('reference'),
              _ref: q.string(),
            })
            .optional()
        ),
        coupon: nullToUndefined(q.string().optional()),
      },
      ["_type == 'pdf'"]: {
        _type: q.literal('pdf'),
        _key: q.string(),
        _ref: q.string(),
      },
      default: {
        _key: q.string(),
        _type: q.literal('externalWebsite'),
        url: q.string(),
      },
    })
}

export const buyingFlowQuery = q('*')
  .filter('_type == "website.buyingFlow" && _id == $id')
  .grab({
    _type: q.literal('website.buyingFlow'),
    slug: ['slug.current', q.string()],
  })
  .slice(0)

export const pdfQuery = q('*')
  .filter('_type in ["multiLangDoc", "doc"] && _id == $id')
  .grab({
    _type: q.union([q.literal('multiLangDoc'), q.literal('doc')]),
    fileName: ['fileName.current', q.string()],
  })
  .slice(0)

const webUrlTypeSchema = z.discriminatedUnion('_type', [
  z.object({
    _type: z.literal('blogPost_2'),
    _key: z.string(),
    post: z.object({
      _type: z.literal('reference'),
      _ref: z.string(),
    }),
    anchor: z.string().nullable().optional(),
  }),
  z.object({
    _key: z.string(),
    _type: z.literal('product'),
    abstractProduct: z.object({
      _type: z.literal('reference'),
      _ref: z.string(),
    }),
    website: z
      .object({
        _type: z.literal('reference'),
        _ref: z.string(),
      })
      .optional(),
    availableEnvironments: z
      .array(z.union([z.literal('staging'), z.literal('production')]))
      .optional(),
  }),
  z.object({
    _type: z.literal('internalWebsite_2'),
    _key: z.string(),
    route: z.object({
      _type: z.literal('reference'),
      _ref: z.string(),
    }),
    anchor: z.string().nullable().optional(),
  }),
  z.object({
    _type: z.literal('buyingFlow'),
    _key: z.string(),
    flow: z
      .object({
        _type: z.literal('reference'),
        _ref: z.string(),
      })
      .optional(),
    campaign: z
      .object({
        _type: z.literal('reference'),
        _ref: z.string(),
      })
      .optional(),
    coupon: z.string().optional(),
  }),
  z
    .object({
      _type: z.literal('pdf'),
      _key: z.string(),
      _ref: z.string(),
    })
    .passthrough(),
  z.object({
    _type: z.literal('externalWebsite'),
    _key: z.string(),
    url: z.string(),
  }),
])

export const webUrlSchema = z.array(webUrlTypeSchema)
export type WebUrl = z.infer<typeof webUrlSchema>
